import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Puzzle, Lightbulb, Pencil, Trophy, Clock, Star } from 'lucide-react';

const diffColors = { 'easy': '#50B86C', 'medium': '#D9A34A', 'hard': '#D94A6B' };

const ActivityPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Activity', color: t.accent };

  const renderMetaBadges = () => (
    <div className="flex items-center gap-2 flex-wrap mb-2">
      {page.difficulty && (
        <span className="text-[7px] font-bold px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: (diffColors[page.difficulty] || t.accent) + '15', color: diffColors[page.difficulty] || t.accent }}>{page.difficulty}</span>
      )}
      {page.timeEstimate && (
        <div className="flex items-center gap-1">
          <Clock className="w-2 h-2" style={{ color: t.subtext }} />
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>{page.timeEstimate}</span>
        </div>
      )}
      {page.materials && page.materials.length > 0 && (
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>
          Needs: {page.materials.join(', ')}
        </span>
      )}
    </div>
  );

  const renderInstructions = () => {
    const instructions = page.activityInstructions || [];
    if (instructions.length === 0) return null;
    return (
      <div className="space-y-1 mb-2">
        {instructions.map((step, si) => (
          <div key={si} className="flex gap-1.5">
            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: t.accent + '15' }}>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', fontWeight: 700, color: t.accent }}>{si + 1}</span>
            </div>
            <p style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text, lineHeight: 1.5 }}>{step}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderCreative = () => (
    <div className="flex-1 flex flex-col">
      {renderMetaBadges()}
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
      {renderInstructions()}
      {/* Drawing/writing space */}
      <div className="flex-1 rounded-xl" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}20`, minHeight: '4rem' }}>
        <div className="flex items-center justify-center h-full">
          <Pencil className="w-5 h-5" style={{ color: t.accent + '25' }} />
        </div>
      </div>
    </div>
  );

  const renderPuzzle = () => (
    <div className="flex-1 flex flex-col">
      {renderMetaBadges()}
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
      {renderInstructions()}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5 mt-1">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.accent + '10' }}>
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', fontWeight: 700, color: t.accent }}>{fi + 1}</span>
              </div>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
              <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderChallenge = () => (
    <div className="flex-1 flex flex-col">
      {renderMetaBadges()}
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
      {renderInstructions()}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center" style={{ borderColor: t.accent + '40' }}>
                <Star className="w-2 h-2" style={{ color: t.accent + '30' }} />
              </div>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCertificate = () => (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
      <Trophy className="w-10 h-10 mb-3" style={{ color: t.accent }} strokeWidth={1.5} />
      <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1.25rem, 3vw, 1.75rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
      {page.content && <p className="mt-2" style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', color: t.subtext }}>{page.content}</p>}
      <div className="mt-4 w-full max-w-[200px]">
        <div className="h-px mb-1" style={{ backgroundColor: t.accent + '40' }} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>Name</span>
      </div>
      <div className="mt-3 w-full max-w-[150px]">
        <div className="h-px mb-1" style={{ backgroundColor: t.accent + '40' }} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext }}>Date</span>
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {renderMetaBadges()}
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {renderInstructions()}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500 }}>{f}</span>
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'activity-creative': renderCreative,
    'activity-puzzle': renderPuzzle,
    'activity-challenge': renderChallenge,
    'activity-game': renderPuzzle,
    'activity-certificate': renderCertificate,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {render()}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default ActivityPage;
